import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { marked } from 'marked';
import { Link, useLocation } from 'react-router-dom';
import { fireConfetti } from '../utils/confetti';
import { apiGet, apiPost } from '../api/client';
import { useVault } from '../components/VaultContext';
import { useAuth } from '../auth/AuthContext';
import WelcomeModal from '../components/WelcomeModal';
import GettingStartedPanel from '../components/GettingStartedPanel';
import type {
  VaultSummaryResponse,
  VaultResponse,
  VaultCountsResponse,
  CreateVaultRequest,
  TermsAndConditionsResponse,
  AcceptInvitationRequest,
  UnreadCountResponse,
} from '../types/api';
import { VaultRole, AccountTier } from '../types/api';
import VaultCard from '../components/VaultCard';
import TrialBanner from '../components/TrialBanner';
import VaultHeader from '../components/VaultHeader';
import ProfilesList from '../components/ProfilesList';
import NotesList from '../components/NotesList';
import DocumentsList from '../components/DocumentsList';
import HeirloomsList from '../components/HeirloomsList';
import FinancialAccountsList from '../components/FinancialAccountsList';
import EmergencyContactsList from '../components/EmergencyContactsList';
import FuneralPlansList from '../components/FuneralPlansList';
import LegacyMessagesList from '../components/LegacyMessagesList';
import VaultChatPanel from '../components/VaultChatPanel';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../components/ToastContext';
import './DashboardPage.css';

type Tab = 'profiles' | 'contacts' | 'funeral' | 'documents' | 'financial' | 'heirlooms' | 'notes' | 'letters' | 'chat';

const WELCOME_KEY_PREFIX = 'cv_welcome_seen_';

export default function DashboardPage() {
  const { addToast } = useToast();
  const { setActiveVault } = useVault();
  const { user } = useAuth();
  const location = useLocation();
  const [vaults, setVaults] = useState<VaultSummaryResponse[]>([]);
  const [selectedVault, setSelectedVault] = useState<VaultResponse | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('profiles');
  const [readinessKey, setReadinessKey] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomePreview, setWelcomePreview] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  function handleTogglePreview() {
    const entering = !isPreviewMode;
    if (entering && activeTab === 'notes') setActiveTab('profiles');
    setIsPreviewMode(entering);
  }

  function handleTabChange(tab: Tab) {
    if (tab !== activeTab) setReadinessKey(k => k + 1);
    setActiveTab(tab);
  }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hadVaultAccess] = useState(() => !!sessionStorage.getItem('hv_had_vaults'));

  // Vault content counts
  const [docCount, setDocCount] = useState<number | null>(null);
  const [heirloomCount, setHeirloomCount] = useState<number | null>(null);
  const [contactCount, setContactCount] = useState<number | null>(null);
  const [funeralPlanCount, setFuneralPlanCount] = useState<number | null>(null);
  const [noteCount, setNoteCount] = useState<number | null>(null);
  const [letterCount, setLetterCount] = useState<number | null>(null);
  const [financialCount, setFinancialCount] = useState<number | null>(null);

  // Create vault
  const [showCreate, setShowCreate] = useState(false);
  const [processingForeverFree, setProcessingForeverFree] = useState(
    () => !!localStorage.getItem('pendingForeverFreeAccept')
  );
  const [processingInvite, setProcessingInvite] = useState(
    () => !!sessionStorage.getItem('pendingInvite')
  );
  const [vaultName, setVaultName] = useState('');
  const [creating, setCreating] = useState(false);

  // Terms & Conditions
  const [terms, setTerms] = useState<TermsAndConditionsResponse | null>(null);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [termsAlreadyAccepted, setTermsAlreadyAccepted] = useState(false);
  const termsContentRef = useRef<HTMLDivElement>(null);

  // Session-aware vault selection — remembers which vault was last used today
  function getLastVaultId(): string | null {
    const date = localStorage.getItem('hv_last_vault_date');
    if (!date || date !== new Date().toDateString()) return null;
    return localStorage.getItem('hv_last_vault_id');
  }

  function saveLastVault(vaultId: string) {
    localStorage.setItem('hv_last_vault_id', vaultId);
    localStorage.setItem('hv_last_vault_date', new Date().toDateString());
  }

  const loadVaults = useCallback(async () => {
    try {
      const data = await apiGet<VaultSummaryResponse[]>('/api/vault');
      setVaults(data);

      if (data.length > 0)
        sessionStorage.setItem('hv_had_vaults', 'true');

      // Auto-select: 1 vault → always enter directly
      // 2+ vaults → enter directly only if same calendar day as last selection
      if (data.length === 1 && !selectedVault) {
        handleSelectVault(data[0].id);
      } else if (data.length > 1 && !selectedVault) {
        const lastId = getLastVaultId();
        if (lastId && data.some(v => v.id === lastId)) {
          handleSelectVault(lastId);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load vaults.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Skip initial load when a ForeverFree accept or vault invite is pending —
    // those effects call loadVaults() themselves after the operation completes,
    // avoiding a race where this earlier response overwrites the vault list with [].
    if (processingForeverFree || processingInvite) return;
    loadVaults();
  }, [loadVaults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Admin preview: ?previewWelcome=1 forces the welcome modal open (admins only)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('previewWelcome') === '1' && user?.isAdmin) {
      setWelcomePreview(true);
      setShowWelcome(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Stripe checkout redirect results
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      addToast('info', 'Payment received! Activating your subscription...');
      // Stripe webhook is async — poll until the vault tier flips to Paid
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const data = await apiGet<VaultSummaryResponse[]>('/api/vault');
        const isPaid = data.some(v => v.accountTier === AccountTier.Paid);
        if (isPaid) {
          setVaults(data);
          addToast('success', 'Subscription activated! Welcome to Cedar Vault.');
        } else if (attempts < 8) {
          setTimeout(poll, 2000);
        } else {
          // Give up polling, reload whatever we have
          setVaults(data);
          addToast('success', 'Subscription activated! Welcome to Cedar Vault.');
        }
      };
      poll();
    } else if (checkout === 'cancelled') {
      addToast('info', 'Checkout cancelled. You can subscribe anytime.');
    }
    // Clean query params from URL without triggering navigation
    if (checkout) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-accept pending invitation from sessionStorage (set by AcceptInvitePage)
  useEffect(() => {
    const raw = sessionStorage.getItem('pendingInvite');
    if (!raw) return;
    sessionStorage.removeItem('pendingInvite');

    (async () => {
      try {
        const { token, acceptedTermsId } = JSON.parse(raw);
        const body: AcceptInvitationRequest = { acceptedTermsId };
        await apiPost(`/api/invite/${token}/accept`, body);
        await loadVaults();
        addToast('success', 'Invitation accepted! Welcome to the vault.');
      } catch (err: any) {
        setError(err.message || 'Failed to accept invitation.');
      } finally {
        setProcessingInvite(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-accept pending Forever Free invite from localStorage (set by AcceptForeverFreePage)
  useEffect(() => {
    const raw = localStorage.getItem('pendingForeverFreeAccept');
    if (!raw) return;
    localStorage.removeItem('pendingForeverFreeAccept');

    (async () => {
      try {
        const { token, acceptedTermsId } = JSON.parse(raw);
        await apiPost(`/api/accept-free-vault/${token}/accept`, { acceptedTermsId });
        addToast('success', 'Welcome! Your Forever Free vault has been created.');
      } catch (err: any) {
        // Silently ignore "already own a vault" — it means the accept already ran
        // (e.g. duplicate tab or retry). loadVaults below will find the vault.
        const msg: string = err.message || '';
        if (!msg.toLowerCase().includes('already')) {
          setError(msg || 'Failed to create your Forever Free vault.');
        }
      } finally {
        // Always reload vaults so any vault created (even on a prior attempt) is found
        await loadVaults();
        setProcessingForeverFree(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSelectVault(vaultId: string) {
    setError('');
    setIsPreviewMode(false);
    setDocCount(null);
    setContactCount(null);
    setFuneralPlanCount(null);
    setHeirloomCount(null);
    setNoteCount(null);
    setLetterCount(null);
    setFinancialCount(null);
    setChatUnread(0);
    saveLastVault(vaultId);
    apiGet<UnreadCountResponse>(`/api/vault/${vaultId}/chat/unread`)
      .then(data => setChatUnread(data.count))
      .catch(() => {});
    try {
      const [vault, counts] = await Promise.all([
        apiGet<VaultResponse>(`/api/vault/${vaultId}`),
        apiGet<VaultCountsResponse>(`/api/vault/${vaultId}/counts`).catch(() => null),
      ]);
      setSelectedVault(vault);
      setActiveTab('profiles');
      const userRole = vaults.find(v => v.id === vaultId)?.userRole ?? null;
      setActiveVault(vault.id, vault.name, vault.accountTier, vault.status, userRole);
      if (userRole === VaultRole.Owner && !localStorage.getItem(WELCOME_KEY_PREFIX + vault.id)) {
        const totalContent = counts
          ? (counts.documents + counts.notes + counts.letters + counts.videos + counts.financialAccounts + counts.heirlooms)
          : 0;
        if (vault.members.length <= 1 && totalContent === 0) {
          setTimeout(() => setShowWelcome(true), 400);
        } else {
          localStorage.setItem(WELCOME_KEY_PREFIX + vault.id, '1');
        }
      }
      if (counts) {
        setDocCount(counts.documents);
        setContactCount(counts.contacts);
        setFuneralPlanCount(counts.funeralPlans);
        setHeirloomCount(counts.heirlooms);
        setNoteCount(counts.notes);
        setLetterCount(counts.letters + counts.videos);
        setFinancialCount(counts.financialAccounts);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load vault.');
    }
  }

  async function handleShowCreate() {
    setShowCreate(true);
    setTermsAccepted(false);
    setTermsScrolled(false);
    setTermsAlreadyAccepted(false);
    setTerms(null);
    setTermsLoading(true);
    setError('');

    try {
      // If the user already accepted current terms (RequireTerms gated them on login),
      // skip showing T&C again — just fetch the ID silently for the API call.
      const pending = await apiGet<TermsAndConditionsResponse | undefined>('/api/terms/pending');
      if (!pending) {
        const current = await apiGet<TermsAndConditionsResponse>('/api/terms/current');
        setTerms(current);
        setTermsAlreadyAccepted(true);
        setTermsAccepted(true);
      } else {
        setTerms(pending);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load terms and conditions.');
    } finally {
      setTermsLoading(false);
    }
  }

  async function handleCreateVault(e: FormEvent) {
    e.preventDefault();
    if (!terms || !termsAccepted) return;

    setError('');
    setCreating(true);

    try {
      const body: CreateVaultRequest = { name: vaultName, acceptedTermsId: terms.id };
      const vault = await apiPost<VaultResponse>('/api/vault', body);
      setVaultName('');
      setShowCreate(false);
      setTerms(null);
      setTermsAccepted(false);
      saveLastVault(vault.id);   // point getLastVaultId at the new vault before loadVaults auto-selects
      await loadVaults();
      if (!localStorage.getItem(WELCOME_KEY_PREFIX + vault.id)) {
        setTimeout(() => setShowWelcome(true), 600);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create vault.');
    } finally {
      setCreating(false);
    }
  }

  function handleCancelCreate() {
    setShowCreate(false);
    setTerms(null);
    setTermsAccepted(false);
    setTermsAlreadyAccepted(false);
    setVaultName('');
    setError('');
  }

  function renderTermsHtml(): string {
    if (!terms) return '';
    return marked(terms.content) as string;
  }

  if (loading || processingForeverFree || processingInvite) return <LoadingSpinner fullPage />;

  const ownsVault = vaults.some(v => v.userRole === VaultRole.Owner);

  // Vault detail view
  if (selectedVault) {
    const vaultUserRole = vaults.find(v => v.id === selectedVault.id)?.userRole ?? VaultRole.Witness;
    const effectiveRole = isPreviewMode ? VaultRole.Witness : vaultUserRole;

    return (
      <div>
        <VaultHeader
          vault={selectedVault}
          userRole={vaultUserRole}
          allVaults={vaults}
          onSwitchVault={handleSelectVault}
          onNameChange={newName => {
            setSelectedVault(sv => sv ? { ...sv, name: newName } : sv);
            setVaults(vs => vs.map(v => v.id === selectedVault.id ? { ...v, name: newName } : v));
          }}
          refreshKey={readinessKey}
          onTabSelect={(tab) => {
            const map: Record<string, Tab> = {
              'profiles': 'profiles',
              'emergency-contacts': 'contacts',
              'funeral-plans': 'funeral',
              'documents': 'documents',
              'financial': 'financial',
              'heirlooms': 'heirlooms',
              'secure-notes': 'notes',
              'letters': 'letters',
            };
            if (map[tab]) handleTabChange(map[tab]);
          }}
          isPreviewMode={isPreviewMode}
          onTogglePreview={handleTogglePreview}
        />

        <TrialBanner
          vault={selectedVault}
          userRole={vaultUserRole}
        />

        {!ownsVault && (
          <div className="create-own-vault-nudge">
            <span>Want to create your own vault?</span>
            <button
              className="create-own-vault-nudge-btn"
              onClick={() => { setSelectedVault(null); setActiveVault(null, null); handleShowCreate(); }}
            >
              Start free for 14 days →
            </button>
          </div>
        )}

        {vaultUserRole === VaultRole.Owner && (
          <GettingStartedPanel
            vaultId={selectedVault.id}
            memberCount={selectedVault.members.length}
            hasContent={(docCount ?? 0) + (noteCount ?? 0) + (letterCount ?? 0) + (financialCount ?? 0) + (heirloomCount ?? 0) > 0}
            onTabSelect={handleTabChange}
            onComplete={() => {
              addToast('success', 'Your vault is ready for your family. Well done.');
              fireConfetti('big');
            }}
          />
        )}

        <AlertMessage type="error" message={error} />

        <div className="vault-detail-tabs">
          <button
            className={`vault-detail-tab${activeTab === 'profiles' ? ' vault-detail-tab--active' : ''}`}
            onClick={() => handleTabChange('profiles')}
          >
            Profiles
            {selectedVault.members.length > 0 && (
              <span className="tab-badge">{selectedVault.members.length}</span>
            )}
          </button>
          <button
            className={`vault-detail-tab${activeTab === 'contacts' ? ' vault-detail-tab--active' : ''}`}
            onClick={() => handleTabChange('contacts')}
          >
            Emergency Contacts
            {contactCount != null && contactCount > 0 && (
              <span className="tab-badge">{contactCount}</span>
            )}
          </button>
          {vaultUserRole !== VaultRole.Advisor && (
          <button
            className={`vault-detail-tab${activeTab === 'financial' ? ' vault-detail-tab--active' : ''}`}
            onClick={() => handleTabChange('financial')}
          >
            Accounts
            {financialCount != null && financialCount > 0 && (
              <span className="tab-badge">{financialCount}</span>
            )}
          </button>
          )}
          <button
            className={`vault-detail-tab${activeTab === 'documents' ? ' vault-detail-tab--active' : ''}`}
            onClick={() => handleTabChange('documents')}
          >
            Documents
            {docCount != null && docCount > 0 && (
              <span className="tab-badge">{docCount}</span>
            )}
          </button>
          <button
            className={`vault-detail-tab${activeTab === 'heirlooms' ? ' vault-detail-tab--active' : ''}`}
            onClick={() => handleTabChange('heirlooms')}
          >
            Possessions
            {heirloomCount != null && heirloomCount > 0 && (
              <span className="tab-badge">{heirloomCount}</span>
            )}
          </button>
          {!isPreviewMode && (vaultUserRole === VaultRole.Owner || vaultUserRole === VaultRole.Spouse) && (
            <button
              className={`vault-detail-tab${activeTab === 'notes' ? ' vault-detail-tab--active' : ''}`}
              onClick={() => handleTabChange('notes')}
            >
              Private Notes
              {noteCount != null && noteCount > 0 && (
                <span className="tab-badge">{noteCount}</span>
              )}
            </button>
          )}
          <button
            className={`vault-detail-tab${activeTab === 'funeral' ? ' vault-detail-tab--active' : ''}`}
            onClick={() => handleTabChange('funeral')}
          >
            Funeral Plans
            {funeralPlanCount != null && funeralPlanCount > 0 && (
              <span className="tab-badge">{funeralPlanCount}</span>
            )}
          </button>
          <button
            className={`vault-detail-tab${activeTab === 'letters' ? ' vault-detail-tab--active' : ''}`}
            onClick={() => handleTabChange('letters')}
          >
            Legacy Messages
            {letterCount != null && letterCount > 0 && (
              <span className="tab-badge">{letterCount}</span>
            )}
          </button>
          {vaultUserRole !== VaultRole.Advisor && (
            <button
              className={`vault-detail-tab vault-detail-tab--chat${activeTab === 'chat' ? ' vault-detail-tab--active' : ''}`}
              onClick={() => handleTabChange('chat')}
            >
              Messages
              {chatUnread > 0 && activeTab !== 'chat' && (
                <span className="tab-badge tab-badge--alert">{chatUnread > 99 ? '99+' : chatUnread}</span>
              )}
            </button>
          )}
        </div>

        {activeTab === 'profiles' && <ProfilesList vault={selectedVault} readOnly={isPreviewMode} onSave={() => setReadinessKey(k => k + 1)} />}
        {activeTab === 'contacts' && (
          <EmergencyContactsList
            vaultId={selectedVault.id}
            userRole={effectiveRole}
            onCountChange={count => { setContactCount(count); setReadinessKey(k => k + 1); }}
          />
        )}
        {activeTab === 'funeral' && (
          <FuneralPlansList
            vaultId={selectedVault.id}
            userRole={effectiveRole}
            onCountChange={count => { setFuneralPlanCount(count); setReadinessKey(k => k + 1); }}
          />
        )}
        {activeTab === 'notes' && (
          isPreviewMode
            ? <div className="preview-notes-hidden"><p>Secure notes are private to vault owners.</p></div>
            : <NotesList
                vaultId={selectedVault.id}
                userRole={effectiveRole}
                onCountChange={count => { setNoteCount(count); setReadinessKey(k => k + 1); }}
              />
        )}
        {activeTab === 'documents' && (
          <DocumentsList
            vaultId={selectedVault.id}
            userRole={effectiveRole}
            onCountChange={count => { setDocCount(count); setReadinessKey(k => k + 1); }}
          />
        )}
        {activeTab === 'financial' && vaultUserRole !== VaultRole.Advisor && (
          <FinancialAccountsList
            vaultId={selectedVault.id}
            userRole={effectiveRole}
            onCountChange={count => { setFinancialCount(count); setReadinessKey(k => k + 1); }}
          />
        )}
        {activeTab === 'heirlooms' && (
          <HeirloomsList
            vaultId={selectedVault.id}
            userRole={effectiveRole}
            onCountChange={count => { setHeirloomCount(count); setReadinessKey(k => k + 1); }}
          />
        )}
        {activeTab === 'letters' && (
          <LegacyMessagesList
            vaultId={selectedVault.id}
            userRole={effectiveRole}
            onCountChange={count => { setLetterCount(count); setReadinessKey(k => k + 1); }}
          />
        )}
        {activeTab === 'chat' && vaultUserRole !== VaultRole.Advisor && (
          <VaultChatPanel
            vaultId={selectedVault.id}
            vaultName={selectedVault.name}
            inline
            onUnreadChange={setChatUnread}
          />
        )}

        {showWelcome && (
          <WelcomeModal
            isPreview={welcomePreview}
            onDismiss={() => {
              setShowWelcome(false);
              if (!welcomePreview && selectedVault) localStorage.setItem(WELCOME_KEY_PREFIX + selectedVault.id, '1');
            }}
          />
        )}
      </div>
    );
  }

  // Vault list / empty state
  if (vaults.length === 0 && !showCreate) {
    if (hadVaultAccess) {
      return (
        <div className="dashboard-empty">
          <h2>Your vault access has changed</h2>
          <p>
            You no longer have access to any vaults. If you believe this is an
            error, please contact the vault owner or{' '}
            <a href="mailto:support@cedarvaultapp.com">support@cedarvaultapp.com</a>.
          </p>
          <AlertMessage type="error" message={error} />
        </div>
      );
    }
    return (
      <div className="dashboard-empty">
        <h2>Welcome to Cedar Vault</h2>
        <p>
          Your secure digital vault for wills, final wishes, and important family
          information. Create your vault to get started.
        </p>
        <AlertMessage type="error" message={error} />
        <button className="btn-primary" onClick={handleShowCreate}>
          Create Your Vault
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="dashboard-title">Your Vaults</h1>

      <AlertMessage type="error" message={error} />

      {showCreate && (
        <form className="create-vault-form create-vault-form--with-terms" onSubmit={handleCreateVault}>
          <div className="form-group">
            <label className="form-label" htmlFor="vaultName">Vault name</label>
            <input
              id="vaultName"
              className="form-input"
              type="text"
              value={vaultName}
              onChange={e => setVaultName(e.target.value)}
              placeholder="e.g., Barrett Family Vault"
              required
              maxLength={200}
              autoFocus
            />
          </div>

          {termsLoading && <LoadingSpinner small />}

          {terms && !termsAlreadyAccepted && (
            <div className="terms-section">
              <h3 className="terms-heading">Terms & Conditions (v{terms.version})</h3>
              <div
                ref={termsContentRef}
                className="terms-content"
                dangerouslySetInnerHTML={{ __html: renderTermsHtml() }}
                onScroll={e => {
                  const el = e.currentTarget;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20)
                    setTermsScrolled(true);
                }}
              />
              {!termsScrolled && (
                <p className="terms-scroll-hint">↓ Please scroll through the terms above to continue</p>
              )}
              <label className="terms-checkbox">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  disabled={!termsScrolled}
                  onChange={e => setTermsAccepted(e.target.checked)}
                />
                <span>I agree to the Terms & Conditions (v{terms.version})</span>
              </label>
            </div>
          )}

          <button
            className="btn-primary"
            type="submit"
            disabled={creating || !termsAccepted || !terms}
          >
            {creating ? <LoadingSpinner small /> : 'Create Vault'}
          </button>
        </form>
      )}

      <div className="vault-list">
        {vaults.map(vault => (
          <VaultCard
            key={vault.id}
            vault={vault}
            onClick={() => handleSelectVault(vault.id)}
          />
        ))}
        {!ownsVault && !showCreate && (
          <div
            className="vault-card vault-card--create-own"
            onClick={handleShowCreate}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && handleShowCreate()}
          >
            <div className="vault-card-create-own-icon">+</div>
            <div>
              <p className="vault-card-create-own-title">Create Your Own Vault</p>
              <p className="vault-card-create-own-sub">Start your own vault — free 14-day trial</p>
            </div>
          </div>
        )}
        {!ownsVault && showCreate && (
          <button className="btn-text" style={{ marginTop: 8 }} onClick={handleCancelCreate}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
